import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  useTheme,
  Paper,
  useMediaQuery,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  alpha,
  Alert,
  Avatar,
  Tooltip,
  Grid,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  ArrowBack,
  Search,
  Clear,
  History,
  Email,
  Person,
  CheckCircle,
  FilterList,
  Star,
  Delete,
  Refresh,
  Coffee,
  CalendarToday,
  MoreVert,
  Visibility,
} from '@mui/icons-material';
import FeedbackActionsModal from '../components/Admin/AdminFeedbackActionModal';
import heroImage from '../assets/feedbacks-WObg.png';
import { AdminTableSkeleton, SCircle } from '../components/skeletons';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Feedback {
  _id: string;
  userId?: string;
  username: string;
  email: string;
  feedback: string;
  timestamp: string;
  status: 'new' | 'reviewed' | 'resolved';
  createdAt: string;
  updatedAt: string;
}

interface UserProfile {
  id: string;
  username: string;
  profilePicture?: string;
}

interface PaginatedResponse {
  error: string;
  success: boolean;
  feedback: Feedback[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  stats?: {
    total: number;
    new: number;
    reviewed: number;
    resolved: number;
  };
}

const AdminFeedback: React.FC = () => {
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [filteredFeedback, setFilteredFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'new' | 'reviewed' | 'resolved'>('all');
  const [sortBy, setSortBy] = useState<'recent' | 'name' | 'status'>('recent');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    reviewed: 0,
    resolved: 0,
  });
  const [userProfiles, setUserProfiles] = useState<Record<string, UserProfile>>({});
  const [profileLoading, setProfileLoading] = useState<Record<string, boolean>>({});

  const { user } = useAuth();
  const theme = useTheme();
  const { mode } = useCustomTheme();
  const navigate = useNavigate();
  
  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  // Glass Effect
  const glassEffect = {
    background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    border: isDark 
      ? '1px solid rgba(196, 182, 111, 0.2)' 
      : '1px solid rgba(5, 24, 20, 0.1)',
    boxShadow: isDark 
      ? '0 20px 60px rgba(0,0,0,0.7)' 
      : '0 20px 60px rgba(5, 24, 20, 0.1)',
  };

  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';

  useEffect(() => {
    if (user) {
      fetchFeedback();
      fetchStats();
    }
  }, [user, page, rowsPerPage, activeTab, searchTerm]);

  useEffect(() => {
    filterAndSortFeedback();
  }, [feedbackList, sortBy]);

  // Update the useEffect that fetches user profiles
useEffect(() => {
  // Fetch user profiles for feedback with userId
  const fetchUserProfiles = async () => {
    const feedbackWithUserId = feedbackList.filter(f => f.userId && f.userId !== 'anonymous');
    const uniqueUserIds = Array.from(new Set(feedbackWithUserId.map(f => f.userId)));
    
    for (const userId of uniqueUserIds) {
      if (userId && !userProfiles[userId] && !profileLoading[userId]) {
        try {
          setProfileLoading(prev => ({ ...prev, [userId]: true }));
          const token = localStorage.getItem('token');
          
          // Try different API endpoints
          let userData = null;
          
          // Try endpoint 1: /api/user/:id
          try {
            const response = await fetch(`${API_URL}/api/user/${userId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });
            
            if (response.ok) {
              const data = await response.json();
              if (data.success && data.user) {
                userData = data.user;
              }
            }
          } catch (err) {
            console.log(`Endpoint /api/user/${userId} failed, trying alternatives`);
          }
          
          // Try endpoint 2: /api/users/:id (as previously used)
          if (!userData) {
            try {
              const response = await fetch(`${API_URL}/api/users/${userId}`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              });
              
              if (response.ok) {
                const data = await response.json();
                if (data.success && data.user) {
                  userData = data.user;
                }
              }
            } catch (err) {
              console.log(`Endpoint /api/users/${userId} failed`);
            }
          }
          
          // Try endpoint 3: Get from all users and filter
          if (!userData) {
            try {
              const response = await fetch(`${API_URL}/api/user/all`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              });
              
              if (response.ok) {
                const data = await response.json();
                if (data.success && data.users) {
                  const foundUser = data.users.find((u: any) => u._id === userId);
                  if (foundUser) {
                    userData = foundUser;
                  }
                }
              }
            } catch (err) {
              console.log('Failed to fetch all users');
            }
          }
          
          if (userData) {
            setUserProfiles(prev => ({
              ...prev,
              [userId]: {
                id: userData._id,
                username: userData.username,
                profilePicture: userData.profilePicture
              }
            }));
          } else {
            console.warn(`Could not find user with ID: ${userId}`);
          }
          
        } catch (err) {
          console.error(`Error fetching profile for user ${userId}:`, err);
        } finally {
          setProfileLoading(prev => ({ ...prev, [userId]: false }));
        }
      }
    }
  };
  
  if (feedbackList.length > 0) {
    fetchUserProfiles();
  }
}, [feedbackList]);

  const fetchFeedback = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        ...(activeTab !== 'all' && { status: activeTab }),
        ...(searchTerm && { search: searchTerm }),
      });

      const response = await fetch(`${API_URL}/api/feedback?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Please login to access admin panel');
        }
        throw new Error(`Failed to fetch feedback: ${response.statusText}`);
      }

      const data: PaginatedResponse = await response.json();
      
      if (data.success) {
        setFeedbackList(data.feedback);
        setTotal(data.pagination.total);
      } else {
        throw new Error(data.error || 'Failed to load feedback');
      }
      
    } catch (err: any) {
      console.error('Error fetching feedback:', err);
      setError(err.message || 'Failed to load feedback data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/feedback/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.stats) {
          setStats(data.stats);
        }
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const filterAndSortFeedback = () => {
    let result = [...feedbackList];
    
    result.sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      
      switch (sortBy) {
        case 'recent':
          return dateB.getTime() - dateA.getTime();
        case 'name':
          return a.username.localeCompare(b.username);
        case 'status':
          return a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });
    
    setFilteredFeedback(result);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
      
      if (diffInSeconds < 60) return 'Just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
      if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
      
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const handleClearSearch = () => setSearchTerm('');
  
  const handleStatusUpdate = async (id: string, newStatus: 'new' | 'reviewed' | 'resolved') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/feedback/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        fetchFeedback();
        fetchStats();
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/feedback/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchFeedback();
        fetchStats();
        setModalOpen(false);
        setSelectedFeedback(null);
      }
    } catch (err) {
      console.error('Error deleting feedback:', err);
    }
  };

  const getInitials = (username: string) => {
    return username
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusColor = (status: string) => {
    // Use theme colors instead of different colors
    return isDark ? goldColor : deepGreen;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'reviewed': return <FilterList fontSize="small" />;
      case 'resolved': return <CheckCircle fontSize="small" />;
      default: return <Star fontSize="small" />;
    }
  };

  const handleRowClick = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setModalOpen(true);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Stats Grid Component for Mobile
  const MobileStatCard = ({ title, value, icon, label, isFirstInRow }: any) => (
    <Grid size={{ xs: 6, sm: 3 }}>
      <Paper
        elevation={0}
        sx={{
          ...glassEffect,
          p: 2,
          borderRadius: '12px',
          mb: 2,
          ml: isFirstInRow ? 0 : 1,
          mr: isFirstInRow ? 1 : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              bgcolor: alpha(goldColor, 0.1),
              borderRadius: '10px',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 40,
            }}
          >
            {React.cloneElement(icon, { 
              sx: { 
                color: goldColor,
                fontSize: 20
              } 
            })}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
                fontSize: '0.7rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: isDark ? '#fff' : deepGreen,
                fontWeight: 800,
                fontSize: '1.25rem',
                lineHeight: 1,
                mt: 0.5,
              }}
            >
              {value}
            </Typography>
            {label && (
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(5,24,20,0.6)',
                  fontSize: '0.65rem',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </Grid>
  );

  // Stats Grid Component for Desktop
  const DesktopStatCard = ({ title, value, icon, label }: any) => (
    <Paper
      elevation={0}
      sx={{
        ...glassEffect,
        p: { xs: 2, md: 3 },
        borderRadius: '16px',
        flex: 1,
        minWidth: { xs: '100%', sm: '45%', md: 'auto' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            bgcolor: alpha(goldColor, 0.1),
            borderRadius: '12px',
            p: { xs: 1, md: 1.5 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon, { 
            sx: { 
              color: goldColor,
              fontSize: { xs: 24, md: 32 }
            } 
          })}
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: isDark ? '#fff' : deepGreen,
              fontWeight: 800,
              fontSize: { xs: '1.5rem', md: '2rem' },
              lineHeight: 1,
            }}
          >
            {value}
          </Typography>
          {label && (
            <Typography
              variant="caption"
              sx={{
                color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(5,24,20,0.6)',
                fontSize: '0.75rem',
              }}
            >
              {label}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );

  const statsData = [
    {
      title: 'Total',
      value: stats.total,
      icon: <Email />,
      label: 'All Feedback',
    },
    {
      title: 'New',
      value: stats.new,
      icon: <Star />,
      label: 'Requires Action',
    },
    {
      title: 'Reviewed',
      value: stats.reviewed,
      icon: <FilterList />,
      label: 'In Progress',
    },
    {
      title: 'Resolved',
      value: stats.resolved,
      icon: <CheckCircle />,
      label: 'Completed',
    },
  ];

  if (!user) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: isDark ? '#000000' : '#FAFAFA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 6, borderRadius: '32px', textAlign: 'center', ...glassEffect }}>
            <Coffee sx={{ fontSize: 80, color: goldColor, mb: 3 }} />
            <Typography variant="h4" sx={{ ...discordFont, color: isDark ? '#FFFFFF' : deepGreen, mb: 2 }}>Admin Access Required</Typography>
            <Typography sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', mb: 4 }}>You need to be logged in as an admin to access this panel.</Typography>
            <Button variant="contained" onClick={() => navigate('/login')} sx={{ background: isDark ? 'rgba(196, 182, 111, 0.9)' : '#051814', color: isDark ? '#051814' : '#FFFFFF', borderRadius: '16px', px: 4, py: 1.5, fontWeight: 700, '&:hover': { background: isDark ? 'rgba(196, 182, 111, 1)' : '#0F3B2E' } }}>Go to Login</Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: isDark ? '#000000' : '#FAFAFA',
      overflowX: 'hidden',
    }}>
      <Container maxWidth="xl" sx={{ 
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
      }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)} 
          sx={{ 
            mb: 3, 
            color: goldColor, 
            '&:hover': { background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)' } 
          }}
        >
          Back
        </Button>

        {/* Header with Image */}
        <Paper sx={{ 
          p: 0, 
          mb: 6, 
          borderRadius: '32px', 
          overflow: 'hidden', 
          ...glassEffect, 
          display: 'flex', 
          flexDirection: { xs: 'column-reverse', md: 'row' }, 
          alignItems: 'center',
          textAlign: { xs: 'center', md: 'left' },
        }}>
          <Box sx={{ 
            p: { xs: 3, md: 6 }, 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' },
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}>
              <History sx={{ fontSize: { xs: 32, md: 40 }, color: goldColor, mr: 2 }} />
              <Typography variant="h3" sx={{ 
                ...discordFont, 
                background: isDark ? 'linear-gradient(135deg, #C4B66F 0%, #FFFFFF 100%)' : `linear-gradient(135deg, ${deepGreen} 0%, ${deepGreen} 100%)`, 
                backgroundClip: 'text', 
                WebkitBackgroundClip: 'text', 
                color: 'transparent', 
                fontSize: { xs: '1.75rem', md: '3rem' },
                textAlign: { xs: 'center', md: 'left' },
              }}>
                Feedback Admin
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ 
              color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(5, 24, 20, 0.8)', 
              fontWeight: 400, 
              maxWidth: 600,
              textAlign: { xs: 'center', md: 'left' },
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}>
              Manage all user feedback, respond to suggestions, and track issues from the WFHCafe Private Beta.
            </Typography>
          </Box>
          <Box sx={{ 
            width: { xs: '100%', md: '40%' }, 
            height: { xs: 180, md: '100%' }, 
            minHeight: { md: 300 }, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            p: { xs: 2, md: 4 } 
          }}>
            <img src={heroImage} alt="Admin Hero" style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'contain', 
              maxHeight: 250 
            }} />
          </Box>
        </Paper>

        {/* Stats Grid - Different layout for mobile */}
        {isMobile ? (
          <Grid container spacing={0} sx={{ mb: 3, width: '100%', mx: 0 }}>
            {statsData.map((stat, index) => (
              <MobileStatCard 
                key={index} 
                {...stat} 
                isFirstInRow={index % 2 === 0}
              />
            ))}
          </Grid>
        ) : (
          <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, mb: 4, flexWrap: 'wrap' }}>
            {statsData.map((stat, index) => (
              <DesktopStatCard key={index} {...stat} />
            ))}
          </Box>
        )}

        {/* Search and Filter */}
        <Paper sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: '20px', 
          ...glassEffect,
          overflow: 'hidden',
        }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search feedback..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.05)',
                  '& fieldset': {
                    borderColor: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: goldColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: goldColor,
                  },
                },
                '& .MuiInputBase-input': {
                  color: isDark ? '#FFFFFF' : deepGreen,
                  fontSize: { xs: '0.95rem', md: '1rem' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: goldColor, opacity: 0.7 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} size="small">
                      <Clear sx={{ fontSize: 18, color: goldColor }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 2, 
            justifyContent: 'space-between', 
            alignItems: { sm: 'center' },
            overflow: 'hidden',
          }}>
            <Box sx={{ 
              width: '100%', 
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
            }}>
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                variant={isMobile ? "scrollable" : "standard"}
                scrollButtons={isMobile ? "auto" : false}
                sx={{
                  '& .MuiTab-root': {
                    color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)',
                    fontWeight: 600,
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: { xs: 1, sm: 2 },
                    fontSize: { xs: '0.85rem', sm: '0.875rem' },
                    '&.Mui-selected': {
                      color: goldColor,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: goldColor,
                    borderRadius: '3px',
                  },
                }}
              >
                <Tab label={`All (${feedbackList.length})`} value="all" />
                <Tab label={`New (${stats.new})`} value="new" />
                <Tab label={`Reviewed (${stats.reviewed})`} value="reviewed" />
                <Tab label={`Resolved (${stats.resolved})`} value="resolved" />
              </Tabs>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              flexShrink: 0,
            }}>
              <Chip
                icon={<History sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                label="Recent"
                onClick={() => setSortBy('recent')}
                size={isMobile ? "small" : "medium"}
                sx={{
                  bgcolor: sortBy === 'recent' ? (isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(196, 182, 111, 0.1)') : 'transparent',
                  color: sortBy === 'recent' ? goldColor : (isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5,24,20,0.7)'),
                  border: `1px solid ${isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(196, 182, 111, 0.2)'}`,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }}
              />
              <Chip
                icon={<Person sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                label="Name"
                onClick={() => setSortBy('name')}
                size={isMobile ? "small" : "medium"}
                sx={{
                  bgcolor: sortBy === 'name' ? (isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(196, 182, 111, 0.1)') : 'transparent',
                  color: sortBy === 'name' ? goldColor : (isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5,24,20,0.7)'),
                  border: `1px solid ${isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(196, 182, 111, 0.2)'}`,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }}
              />
            </Box>
          </Box>
        </Paper>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: '12px',
              ...glassEffect,
            }}
            action={
              <Button color="inherit" size="small" onClick={fetchFeedback}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Feedback Table */}
        <Paper sx={{ 
          p: { xs: 2, sm: 3 }, 
          borderRadius: '20px', 
          ...glassEffect,
          overflow: 'hidden',
        }}>
          {loading ? (
            <AdminTableSkeleton rows={8} cols={5} />
          ) : filteredFeedback.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Email sx={{ fontSize: 80, color: goldColor, mb: 3, opacity: 0.5 }} />
              <Typography variant="h4" sx={{ ...discordFont, color: isDark ? '#FFFFFF' : deepGreen, mb: 2 }}>{searchTerm ? 'No Matching Feedback' : 'No Feedback Yet'}</Typography>
              <Typography sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', mb: 4, maxWidth: 400, mx: 'auto' }}>{searchTerm ? `No feedback found matching "${searchTerm}".` : "No feedback has been submitted yet."}</Typography>
              <Button variant="contained" onClick={() => searchTerm ? setSearchTerm('') : fetchFeedback} sx={{ background: isDark ? 'rgba(196, 182, 111, 0.9)' : '#051814', color: isDark ? '#051814' : '#FFFFFF', borderRadius: '16px', px: 4, py: 1.5, fontWeight: 700, '&:hover': { background: isDark ? 'rgba(196, 182, 111, 1)' : '#0F3B2E' } }}>{searchTerm ? 'Clear Search' : 'Refresh'}</Button>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ 
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                scrollbarWidth: 'none',
              }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow sx={{ 
                      '& th': { 
                        borderBottom: `2px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
                        color: isDark ? goldColor : deepGreen,
                        fontWeight: 700,
                        fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                        py: { xs: 1, sm: 2 },
                        px: { xs: 1, sm: 2 },
                        whiteSpace: 'nowrap',
                      }
                    }}>
                      <TableCell>User</TableCell>
                      <TableCell>Feedback</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredFeedback.map((feedback) => {
                      const userProfile = feedback.userId ? userProfiles[feedback.userId] : null;
                      const isUserFeedback = feedback.userId && feedback.userId !== 'anonymous';
                      const isLoadingProfile = feedback.userId ? profileLoading[feedback.userId] : false;
                      
                      return (
                        <TableRow 
                          key={feedback._id}
                          hover
                          onClick={() => handleRowClick(feedback)}
                          sx={{ 
                            cursor: 'pointer',
                            borderRadius: '12px',
                            '&:hover': { 
                              backgroundColor: alpha(isDark ? goldColor : deepGreen, isDark ? 0.05 : 0.02),
                              '& td:first-of-type': {
                                borderTopLeftRadius: '12px',
                                borderBottomLeftRadius: '12px',
                              },
                              '& td:last-of-type': {
                                borderTopRightRadius: '12px',
                                borderBottomRightRadius: '12px',
                              },
                            },
                            '& td': {
                              borderBottom: `1px solid ${alpha(isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)', 0.3)}`,
                              py: { xs: 1.5, sm: 2 },
                              px: { xs: 1, sm: 2 },
                              transition: 'background-color 0.2s ease',
                            },
                            ...(isUserFeedback && {
                              bgcolor: alpha(isDark ? goldColor : deepGreen, isDark ? 0.02 : 0.01),
                              '&:hover': {
                                bgcolor: alpha(isDark ? goldColor : deepGreen, isDark ? 0.07 : 0.03),
                              }
                            })
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                              {isLoadingProfile ? (
                                <SCircle size={32} />
                              ) : (
                                <Avatar
                                  src={userProfile?.profilePicture}
                                  sx={{
                                    bgcolor: alpha(goldColor, 0.1),
                                    color: goldColor,
                                    fontWeight: 700,
                                    width: { xs: 40, sm: 48 },
                                    height: { xs: 40, sm: 48 },
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                  }}
                                >
                                  {getInitials(feedback.username)}
                                </Avatar>
                              )}
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body1" sx={{ 
                                  fontWeight: 700,
                                  color: isDark ? '#fff' : deepGreen,
                                  fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}>
                                  {feedback.username}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(5,24,20,0.8)',
                                  fontSize: { xs: '0.75rem', sm: '0.95rem' },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}>
                                  {feedback.email}
                                </Typography>
                                {feedback.userId && (
                                  <Typography variant="caption" sx={{ 
                                    color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(5,24,20,0.6)',
                                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                  }}>
                                    ID: {feedback.userId.substring(0, 8)}...
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ maxWidth: { xs: 150, sm: 300 } }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(5,24,20,0.9)',
                                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                fontWeight: 500,
                                lineHeight: 1.4,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                ...(isUserFeedback && {
                                  fontWeight: 600,
                                })
                              }}
                            >
                              {feedback.feedback}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarToday sx={{ 
                                fontSize: { xs: 16, sm: 20 }, 
                                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)' 
                              }} />
                              <Typography variant="body1" sx={{ 
                                color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(5,24,20,0.9)',
                                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                              }}>
                                {formatDate(feedback.timestamp)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(feedback.status)}
                              label={feedback.status}
                              size={isMobile ? "small" : "medium"}
                              sx={{
                                bgcolor: alpha(getStatusColor(feedback.status), isDark ? 0.15 : 0.1),
                                color: getStatusColor(feedback.status),
                                fontWeight: 700,
                                fontSize: { xs: '0.8rem', sm: '0.95rem' },
                                textTransform: 'capitalize',
                                border: `2px solid ${alpha(getStatusColor(feedback.status), 0.3)}`,
                                height: { xs: 28, sm: 36 },
                                '& .MuiChip-icon': {
                                  color: getStatusColor(feedback.status),
                                  fontSize: { xs: 16, sm: 18 },
                                }
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: `1px solid ${alpha(
                    isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
                    0.3
                  )}`,
                  mt: 2,
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    color: isDark
                      ? 'rgba(255,255,255,0.7)'
                      : 'rgba(5,24,20,0.7)',
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                    display: { xs: 'none', sm: 'inline-flex' },
                  },
                  '& .MuiTablePagination-toolbar': {
                    justifyContent: 'flex-end',
                    overflowX: 'hidden',
                    flexWrap: 'nowrap',
                  },
                  '& .MuiTablePagination-spacer': {
                    display: 'none',
                  },
                  '& ::-webkit-scrollbar': {
                    display: 'none',
                  },
                  scrollbarWidth: 'none',
                }}
              />
            </>
          )}
        </Paper>
      </Container>

      {/* Feedback Actions Modal */}
      <FeedbackActionsModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedFeedback(null);
        }}
        feedback={selectedFeedback}
        onStatusUpdate={handleStatusUpdate}
        onDelete={handleDelete}
        isDark={isDark}
      />
    </Box>
  );
};

export default AdminFeedback;